import { useEffect, useState } from "react";
import { Button, Space, Switch, Table } from "antd";
import { MasterDataServices } from "../../../../services/master-data";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen } from "@fortawesome/free-solid-svg-icons";
import { useMessageApi } from "../../../../context/MessageContext";
import { PERMISSIONS } from "../../../../constants/permission";
import { useHasPermission } from "../../../../hooks/useHasPermission";
import { IMasterData, IMasterDataListResponse } from "../../../../interfaces/master-data";
import ContentCellNumber from "../../../../components/table/content-cell-number";
import { DEFAULT_PAGINATION } from "../../../../constants/pagination";
import { ISortInfo } from "../../../../interfaces/common";

interface ListCurrencyProps {
  onEditCurrency: (currency: IMasterData) => void;
  trigger: boolean;
  onDoneTrigger: () => void;
  debouncedSearchValue: string | null;
  showActiveOnly: boolean | null;
}

const CurrencyList: React.FC<ListCurrencyProps> = ({
  onEditCurrency,
  trigger,
  onDoneTrigger,
  debouncedSearchValue,
  showActiveOnly
}) => {
  const messageApi = useMessageApi();
  const [currencies, setCurrencies] = useState<IMasterData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const hasPermissionEditor = useHasPermission([PERMISSIONS.MASTER_DATA_EDITOR]);

  const [sortInfo, setSortInfo] = useState<ISortInfo>({});
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: DEFAULT_PAGINATION.pageSize,
    total: 0,
  });
  
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchData = async () => {
    try {
      setLoading(true);
      const params = {
        keyword: debouncedSearchValue || undefined,
        type: 'CURRENCY',
        page: pagination.current,
        pageSize: pagination.pageSize,
        orderBy: sortInfo.orderBy ?? "code",
        orderDirection: sortInfo.orderDirection ?? "ASC",
        showActiveOnly: showActiveOnly,
      };
      const response: IMasterDataListResponse = await MasterDataServices.getList(params);
      if (response.success) {
        setCurrencies(response.data.results);
        setPagination((prev) => ({
          ...prev,
          total: response.data.meta?.totalElements || 0,
        }));
      }
    } catch (error) {
      console.error("Fetch currencies error:", error);
    } finally {
      setLoading(false);
      onDoneTrigger();
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchValue, pagination.current, pagination.pageSize, sortInfo, trigger, showActiveOnly]);



  const handleTableChange = (paginationConfig: any, filters: any, sorter: any) => {
    // Handle pagination
    setPagination((prev) => ({
      ...prev,
      current: paginationConfig.pageSize !== prev.pageSize ? 1 : paginationConfig.current,
      pageSize: paginationConfig.pageSize,
    }));

    // Handle sorting
    if (sorter?.field) {
      setSortInfo({
        orderBy: sorter.field,
        orderDirection: sorter.order === "ascend" ? "asc" : "desc",
      });
    } else {
      // Clear sorting
      setSortInfo({});
    }
  };

  const columns = [
    {
      title: "Currency Code",
      dataIndex: "code",
      key: "code",
      sorter: true,
    },
    {
      title: "Currency Name",
      dataIndex: "label",
      key: "label",
      sorter: true,
    },
    {
      title: "Tolerance (%)",
      render: (_: any, record: IMasterData) => <ContentCellNumber value={record.value?.tolerancePercent} />
    },
    {
      title: "Tolerance (Absolute)",
      render: (_: any, record: IMasterData) => <ContentCellNumber value={record.value?.toleranceAbsolute} />
    },
    {
      title: "Exchange Rate",
      render: (_: any, record: IMasterData) => <ContentCellNumber value={record.value?.exchangeRate} />
    },
    {
      title: "Active",
      dataIndex: "isActive",
      key: "isActive",
      render: (_: any, record: IMasterData) => (
        <Switch
          checked={record.isActive}
          disabled={!hasPermissionEditor}
          onChange={() => handleIsActivesCurrency(record)}
        />
      ),
    },
    {
      title: "Actions",
      key: "action",
      width: 100,
      render: (_: any, record: IMasterData) =>
        hasPermissionEditor ? (
          <Space size="small" className="box-action" align="center">
            <Button type="link" onClick={() => onEditCurrency(record)}>
              <FontAwesomeIcon icon={faPen} />
            </Button>
          </Space>
        ) : null,
    },
  ];


  const handleIsActivesCurrency = async (selectCurrency: IMasterData) => {
    try {
      const newStatus = !selectCurrency.isActive;
      const res: any = await MasterDataServices.update(
        selectCurrency?.id?.toString(),
        {
          type: selectCurrency.type,
          code: selectCurrency.code,
          isActive: newStatus
        }
      );
      if (res?.success) {
        messageApi.success(`Successfully ${selectCurrency.isActive ? 'deactivated' : 'activated'}`);
        setCurrencies(prev =>
          prev.map(item =>
            item.id === selectCurrency.id ? { ...item, isActive: newStatus } : item
          )
        );
      }
    } catch (error: any) {
      messageApi.error(error?.message);
    }
  };

  return (
    <Table
      dataSource={currencies}
      loading={loading}
      rowKey={(record) => record.id}
      columns={columns}
      pagination={{
        current: pagination.current,
        pageSize: pagination.pageSize,
        total: pagination.total,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (total, range) => `Total ${total} items`,
      }}
      onChange={handleTableChange}
      size="small"
      className="table-fm"
      rowClassName={(record, index) =>
        index % 2 === 0 ? "highlight-row" : ""
      }
    />
  );
};

export default CurrencyList;
